// Every variable of the living sky in one place (Night Sky Construction:
// "each day has its own animated-bit variables"). Home and play share these
// values, so they are tuned for play, where nothing may compete with the
// letters. Starting values, to tune with the tuning panel (?tune on dev and
// staging), whose Copy values button writes this object back out.
import type { Weekday } from '../../../systems/api/types';

export interface AmbientDay {
  /** Share of the visible background stars twinkling at any moment. */
  twinkleShare: number;
  /** Mean seconds between shooting stars. */
  shootingStarEveryS: number;
  /** Mean seconds between satellites; null for none. */
  satelliteEveryS: number | null;
}

export interface AmbientConfig {
  drift: {
    /** Sky units per second on home (1000 units is one screen height). */
    speed: number;
    /** Which way the stars move across the screen. */
    direction: 'left' | 'right';
    /** Share of the drift each depth layer takes, far, mid, near. The constellations ride the near layer. */
    layerFactors: [number, number, number];
    /** How long the drift takes to come to a stop when leaving home. */
    settleMs: number;
    /** How long it takes to gather speed on arriving home. */
    easeInMs: number;
  };
  parallax: {
    /** Share of the board's pan each depth layer takes during play, far, mid, near. */
    panFactors: [number, number, number];
  };
  twinkle: {
    /** A twinkle lasts between these many seconds. */
    minS: number;
    maxS: number;
    /** How far the star swells toward full brightness (0–1); it dims by half as much first. */
    depth: number;
    /** How much larger the star draws at its brightest. */
    scale: number;
  };
  shootingStar: {
    /** ± share of the mean interval, so they never keep time. */
    jitter: number;
    durationMs: number;
    /** Tail length and distance travelled, sky units. */
    length: number;
    travel: number;
    /** Peak opacity, 0–1. */
    brightness: number;
    /** Line width, px. */
    width: number;
    /** Degrees below the horizontal. */
    minAngle: number;
    maxAngle: number;
    /** Share of the width at each side where one may start ("at the edges"). */
    edgeBand: number;
  };
  satellite: {
    jitter: number;
    /** Seconds to cross the screen. */
    crossingS: number;
    brightness: number;
    /** Diameter, px. */
    size: number;
  };
  constellations: {
    /** Line opacity, 0–1. */
    opacity: number;
    /** Line width, px. */
    lineWidth: number;
    /** Scale on the atlas star symbol (9 px for the brightest at 1). */
    starSize: number;
  };
  transition: {
    /** Home UI and constellations, or the play HUD, fading out before the pan. */
    fadeOutMs: number;
    /** The pan between the halves. */
    panMs: number;
    easing: string;
    /** The play HUD fading in after landing. */
    hudFadeMs: number;
    /** The home UI and constellations fading in after landing. */
    homeFadeMs: number;
  };
  days: Record<Weekday, AmbientDay>;
}

export const AMBIENT_DEFAULTS: AmbientConfig = {
  drift: { speed: 8, direction: 'right', layerFactors: [0.55, 0.78, 1], settleMs: 900, easeInMs: 4000 },
  parallax: { panFactors: [0.01, 0.02, 0.04] },
  twinkle: { minS: 2, maxS: 4, depth: 0.6, scale: 1.5 },
  shootingStar: {
    jitter: 0.5,
    durationMs: 800,
    length: 220,
    travel: 380,
    brightness: 0.85,
    width: 1.5,
    minAngle: 18,
    maxAngle: 38,
    edgeBand: 0.28,
  },
  satellite: { jitter: 0.4, crossingS: 40, brightness: 0.5, size: 2.2 },
  constellations: { opacity: 0.5, lineWidth: 1, starSize: 1 },
  transition: { fadeOutMs: 450, panMs: 1800, easing: 'cubic-bezier(0.65, 0, 0.35, 1)', hudFadeMs: 450, homeFadeMs: 700 },
  days: {
    monday: { twinkleShare: 0.02, shootingStarEveryS: 360, satelliteEveryS: null },
    tuesday: { twinkleShare: 0.025, shootingStarEveryS: 300, satelliteEveryS: 360 },
    wednesday: { twinkleShare: 0.03, shootingStarEveryS: 240, satelliteEveryS: null },
    thursday: { twinkleShare: 0.035, shootingStarEveryS: 240, satelliteEveryS: null },
    friday: { twinkleShare: 0.04, shootingStarEveryS: 180, satelliteEveryS: 360 },
    saturday: { twinkleShare: 0.045, shootingStarEveryS: 180, satelliteEveryS: null },
    sunday: { twinkleShare: 0.05, shootingStarEveryS: 120, satelliteEveryS: 360 },
  },
};
